import { type ChangeEvent, createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { trackEvent } from '@zep/logger/tracking';
import { AcceptAiFileExtensions } from '@/shared/features/ai-quiz-generation/consts';
import { alertDialog } from '@/ui_v3';
import { useAiFileChange } from '@/views/build/features/ai-prompt-attachment';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { useAiBuilderComposerFilePickerSignal } from '../hooks/useAiBuilderComposerFilePickerSignal';
import { useAiBuilderChatActions, useAiBuilderChatComposerSelector, useAiBuilderChatSessionIdSafe } from '../stores';
import { deriveAiBuilderComposerPolicy } from '../utils/aiBuilderComposerPolicy';
import type { PdfPageRange } from '../utils/pdfPageRanges';
import { AiBuilderPdfPageSelectionModal, type PdfPreviewTerminalFailure } from './AiBuilderPdfPageSelectionModal';
type AiBuilderAttachmentPreparationContextValue = {
  canOpenFilePicker: boolean;
  openFilePicker: () => void;
  prepareFile: (file: File | undefined) => void;
};
type PendingPdfSelection = {
  file: File;
};
const AiBuilderAttachmentPreparationContext = createContext<AiBuilderAttachmentPreparationContextValue | null>(null);
const isPdfFile = (file: File) => file.type.toLowerCase() === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
export const AiBuilderAttachmentPreparationProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const {
    draft,
    isGenerating,
    messages,
    panelOpen,
    placeholder,
    selectedFile,
    turnInProgress,
    usage
  } = useAiBuilderChatComposerSelector();
  const {
    setSelectedFile
  } = useAiBuilderChatActions();
  const copy = useAiBuilderChatCopy();
  const sessionId = useAiBuilderChatSessionIdSafe();
  const validateAiFile = useAiFileChange();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const validationRequestIdRef = useRef(0);
  const attachmentPreparationRef = useRef(false);
  const [isPreparingAttachment, setIsPreparingAttachment] = useState(false);
  const [pendingPdfSelection, setPendingPdfSelection] = useState<PendingPdfSelection | null>(null);
  const [pendingPdfFailure, setPendingPdfFailure] = useState<PdfPreviewTerminalFailure | null>(null);
  const terminalFailureHandledRef = useRef(false);
  const terminalAlertOpenedRef = useRef(false);
  const composerPolicy = deriveAiBuilderComposerPolicy({
    draft,
    hasSelectedFile: !!selectedFile,
    isGenerating,
    messages,
    placeholder,
    copy,
    turnInProgress,
    usage
  });
  const canAttachFile = composerPolicy.canUseSourceAttachment && !composerPolicy.hasActiveTurn && !selectedFile;
  const canAttachFileRef = useRef(canAttachFile);
  canAttachFileRef.current = canAttachFile;
  const canOpenFilePicker = canAttachFile && !isPreparingAttachment;
  const setAttachmentPreparation = useCallback((preparing: boolean) => {
    attachmentPreparationRef.current = preparing;
    setIsPreparingAttachment(preparing);
  }, []);
  useEffect(() => {
    if (canAttachFile) return;
    validationRequestIdRef.current += 1;
    setAttachmentPreparation(false);
    setPendingPdfSelection(null);
  }, [canAttachFile, setAttachmentPreparation]);
  useEffect(() => {
    if (!pendingPdfFailure || pendingPdfSelection || terminalAlertOpenedRef.current) {
      return;
    }
    terminalAlertOpenedRef.current = true;
    const failureCopy = copy.pdfPageSelection.previewFailure;
    const body = pendingPdfFailure === 'password' ? failureCopy.password : pendingPdfFailure === 'thumbnail' ? failureCopy.thumbnail : failureCopy.invalid;
    alertDialog.open({
      header: {
        title: failureCopy.title
      },
      body
    });
    setPendingPdfFailure(null);
  }, [copy, pendingPdfFailure, pendingPdfSelection]);
  const openFilePicker = useCallback(() => {
    if (!canAttachFileRef.current || attachmentPreparationRef.current) return;
    fileInputRef.current?.click();
  }, []);
  useAiBuilderComposerFilePickerSignal({
    discarded: !canOpenFilePicker,
    fileInputRef,
    panelOpen,
    suppressed: !!composerPolicy.activeChoiceMessage || !!composerPolicy.activeConfirmMessage
  });
  const prepareFile = useCallback((file: File | undefined) => {
    if (!canAttachFileRef.current || attachmentPreparationRef.current || !file) {
      return;
    }
    setAttachmentPreparation(true);
    const requestId = validationRequestIdRef.current + 1;
    validationRequestIdRef.current = requestId;
    void validateAiFile(file).then(validFile => {
      if (validationRequestIdRef.current !== requestId) return;
      if (!canAttachFileRef.current) return;
      if (!validFile) {
        trackEvent('quiz_ai_builder_source_attach', {
          source_type: 'file',
          status: 'invalid',
          session_id: sessionId ?? ''
        });
        setAttachmentPreparation(false);
        return;
      }
      if (isPdfFile(validFile)) {
        terminalFailureHandledRef.current = false;
        terminalAlertOpenedRef.current = false;
        setPendingPdfFailure(null);
        setPendingPdfSelection({
          file: validFile
        });
        return;
      }
      setAttachmentPreparation(false);
      setSelectedFile({
        file: validFile
      });
    }).catch(() => {
      if (validationRequestIdRef.current !== requestId) return;
      setAttachmentPreparation(false);
    });
  }, [sessionId, setAttachmentPreparation, setSelectedFile, validateAiFile]);
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    prepareFile(file);
  };
  const savePdfSelection = (pageRanges: PdfPageRange[], pageCount: number) => {
    if (!canAttachFileRef.current || !pendingPdfSelection) return;
    setSelectedFile({
      file: pendingPdfSelection.file,
      pageCount,
      pageRanges
    });
    setAttachmentPreparation(false);
    setPendingPdfSelection(null);
  };
  const chooseAnotherFile = () => {
    setAttachmentPreparation(false);
    setPendingPdfSelection(null);
    window.requestAnimationFrame(() => {
      if (!canAttachFileRef.current || attachmentPreparationRef.current) return;
      fileInputRef.current?.click();
    });
  };
  const cancelPdfSelection = () => {
    setAttachmentPreparation(false);
    setPendingPdfSelection(null);
  };
  const handlePdfPreviewTerminalFailure = useCallback((failure: PdfPreviewTerminalFailure) => {
    if (terminalFailureHandledRef.current) return;
    terminalFailureHandledRef.current = true;
    setAttachmentPreparation(false);
    setPendingPdfSelection(null);
    setPendingPdfFailure(failure);
  }, [setAttachmentPreparation]);
  return <AiBuilderAttachmentPreparationContext.Provider value={{
    canOpenFilePicker,
    openFilePicker,
    prepareFile
  }} data-sentry-element="unknown" data-sentry-component="AiBuilderAttachmentPreparationProvider" data-sentry-source-file="AiBuilderAttachmentPreparationProvider.tsx">
      <input ref={fileInputRef} type="file" hidden accept={AcceptAiFileExtensions} onChange={handleFileChange} />
      {children}
      {canAttachFile && pendingPdfSelection && <AiBuilderPdfPageSelectionModal file={pendingPdfSelection.file} open onCancel={cancelPdfSelection} onChooseAnotherFile={chooseAnotherFile} onSave={savePdfSelection} onTerminalFailure={handlePdfPreviewTerminalFailure} />}
    </AiBuilderAttachmentPreparationContext.Provider>;
};
export const useAiBuilderAttachmentPreparation = () => {
  const value = useContext(AiBuilderAttachmentPreparationContext);
  if (!value) {
    throw new Error('useAiBuilderAttachmentPreparation must be used within AiBuilderAttachmentPreparationProvider');
  }
  return value;
};